<template>
  <div class="mb-6">
    <div class="d-flex justify-space-between align-center mb-4">
      <h3 class="text-h6 font-weight-bold white--text">Management Übersicht</h3>
      <v-btn icon small @click="fetchData" :loading="loading">
        <v-icon color="grey">mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-row v-if="loading" class="d-flex justify-center my-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-row>

    <v-row v-else dense>
      <v-col cols="12" md="4">
        <v-card color="#1e1e1e" dark class="pa-4 fill-height elevation-3" style="border-left: 4px solid #2196f3">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="grey--text text--lighten-1 text-subtitle-2 text-uppercase">Mitglieder Aktuell</span>
            <v-icon color="blue">mdi-account-group</v-icon>
          </div>

          <div class="d-flex align-baseline">
            <span class="text-h4 font-weight-bold white--text mr-2">
              {{ formatNumber(stats.currentIst) }}
            </span>
          </div>

          <div class="d-flex align-center mt-3">
            <span class="caption grey--text mr-2"> Plan: {{ formatNumber(stats.currentPlan) }} </span>
            <v-chip x-small label class="font-weight-bold" :color="getDiffColor(stats.currentIst, stats.currentPlan)">
              {{ getDiffPercent(stats.currentIst, stats.currentPlan) }}%
            </v-chip>
          </div>

          <v-divider class="my-3" style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>

          <div class="d-flex justify-space-between align-center">
            <span class="caption grey--text">Veränderung (vs. Vormonat):</span>

            <div :class="getDiffColorClass(stats.diffAbs)" class="font-weight-bold d-flex align-center">
              <v-icon small :color="getDiffColor(stats.diffAbs, 0)" class="mr-1">
                {{ stats.diffAbs >= 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
              </v-icon>
              <span class="text-subtitle-1"> {{ stats.diffAbs > 0 ? "+" : "" }}{{ formatNumber(stats.diffAbs) }} </span>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card color="#1e1e1e" dark class="pa-4 fill-height elevation-3" style="border-left: 4px solid #4caf50">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="grey--text text--lighten-1 text-subtitle-2 text-uppercase">Sales (Offen)</span>
            <v-icon color="green">mdi-file-document-edit</v-icon>
          </div>

          <v-row no-gutters class="mt-1">
            <v-col cols="6" class="pr-2 border-right">
              <div class="caption grey--text">Heute</div>
              <div class="text-h5 font-weight-bold">{{ stats.today }}</div>
            </v-col>
            <v-col cols="6" class="pl-3">
              <div class="caption grey--text">Gestern</div>
              <div class="text-h5 font-weight-bold">{{ stats.yesterday }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-3" style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>

          <div class="d-flex justify-space-between align-center">
            <span class="red--text text--lighten-1 font-weight-medium caption">
              <v-icon small color="red" class="mr-1">mdi-alert-circle-outline</v-icon>
              Offene Verträge Total
            </span>
            <v-chip small color="red darken-2" class="font-weight-bold white--text">
              {{ stats.open }}
            </v-chip>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card color="transparent" elevation="0" class="fill-height d-flex flex-column justify-space-between">
          <v-btn
            block
            large
            dark
            color="blue-grey darken-3"
            class="mb-3 justify-start py-6 elevation-2"
            @click="$router.push('/dashboard/contract-development')">
            <v-icon left size="24" class="mr-3">mdi-chart-bar</v-icon>
            <div class="d-flex flex-column align-start">
              <span class="font-weight-bold subtitle-2">Aboentwicklung</span>
              <span class="caption grey--text text--lighten-2">Detailansicht öffnen</span>
            </div>
          </v-btn>

          <v-btn
            block
            large
            dark
            color="blue-grey darken-3"
            class="justify-start py-6 elevation-2"
            @click="$router.push('/dashboard/transaction-statistics')">
            <v-icon left size="24" class="mr-3">mdi-cash-multiple</v-icon>
            <div class="d-flex flex-column align-start">
              <span class="font-weight-bold subtitle-2">Transaktionen</span>
              <span class="caption grey--text text--lighten-2">Umsätze prüfen</span>
            </div>
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState } from "vuex"
import api from "@/api"

export default {
  name: "ManagerDashboard",
  data() {
    return {
      loading: true,
      stats: {
        currentIst: 0,
        currentPlan: 0,
        prevIst: 0,
        diffAbs: 0,
        today: 0,
        yesterday: 0,
        open: 0,
      },
    }
  },
  computed: {
    // Zugriff auf Vuex für die Plan-Daten der Studios
    ...mapState("studioStore", ["storeStudios"]),
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        // Parallel beide Datenquellen abrufen
        const [devResponse, miniResponse] = await Promise.all([
          api.fetchContractDevelopment(), // Holt Cronjob-Daten (JSON)
          api.fetchHomeMiniStats(), // Holt Live-Daten (Aggregation)
        ])

        // Verarbeite die Cronjob Daten für Card 1
        if (devResponse && devResponse.centers) {
          this.processContractData(devResponse.centers)
        }

        // Verarbeite die Live Daten für Card 2
        this.stats.today = miniResponse.today || 0
        this.stats.yesterday = miniResponse.yesterday || 0
        this.stats.open = miniResponse.open || 0
      } catch (error) {
        console.error("Fehler beim Laden des Manager Dashboards:", error)
      } finally {
        this.loading = false
      }
    },

    processContractData(centersData) {
      if (!centersData) return

      const now = new Date()
      // Wir brauchen den Key für den aktuellen Monat, um IST anzuzeigen
      const currentKey = this.getDateKey(now)

      const prevDate = new Date()
      prevDate.setMonth(prevDate.getMonth() - 1)
      const prevKey = this.getDateKey(prevDate)

      let totalIst = 0
      let totalPrev = 0
      let totalPlan = 0

      // Summiere über alle Center
      Object.keys(centersData).forEach((centerId) => {
        const centerMonths = centersData[centerId]

        // Werte aus der DB holen (Fallback auf 0)
        const istVal = centerMonths[currentKey] || 0
        const prevVal = centerMonths[prevKey] || 0

        totalIst += istVal
        totalPrev += prevVal

        // Plan Berechnung basierend auf Vuex Store Daten (analog zur Detail-Ansicht)
        if (this.storeStudios && this.storeStudios.length > 0) {
          const storeCenter = this.storeStudios.find((s) => s.code === centerId)
          if (storeCenter) {
            // Finde geplantes Wachstum für diesen Monat
            const growthData = storeCenter.growth ? storeCenter.growth.find((g) => g.date === currentKey) : null

            // Simpler Plan: Vormonat IST + Geplantes Wachstum
            let planVal = prevVal
            if (growthData) {
              planVal += Number(growthData.count)
            }
            totalPlan += planVal
          }
        }
      })

      this.stats.currentIst = totalIst
      this.stats.prevIst = totalPrev
      this.stats.currentPlan = totalPlan

      // Absolute Differenz berechnen (Aktuell - Vormonat)
      this.stats.diffAbs = totalIst - totalPrev
    },

    getDateKey(date) {
      // Formatiert Date zu "YYYY-MM"
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    },

    formatNumber(val) {
      return val ? val.toLocaleString("de-CH") : "0"
    },

    // Farbe für Plan-Erreichung (Chip)
    getDiffColor(ist, plan) {
      if (!plan) return "grey"
      return ist >= plan ? "success" : "error"
    },

    // Farbe für Veränderungstext (Grün bei Wachstum, Rot bei Verlust)
    getDiffColorClass(val) {
      if (val === 0) return "grey--text"
      return val > 0 ? "success--text" : "error--text"
    },

    // Prozentuale Abweichung vom Plan
    getDiffPercent(ist, plan) {
      if (!plan || plan === 0) return 0
      const diff = ist - plan
      // Bei Plan 0 könnte man 100% zurückgeben, hier einfach 0
      return ((diff / plan) * 100).toFixed(1)
    },
  },
}
</script>

<style scoped>
.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
